import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import apiRequests from 'src/utils/api';
import apiRoutes from 'src/utils/apiRoutes';
import { Reaction } from 'src/utils/reaction';

const useCommentReactions = ({
  initialData,
  onReactAdd,
  onReactRemove,
}: any) => {
  const user = useSelector((store: any) => store.auth.user);

  const initialUserReactions = useMemo(
    () =>
      initialData?.reactions?.filter(
        (el: Reaction) => el?.creator?.uuid === user?.uuid
      ) || [],
    [initialData?.reactions, user?.uuid]
  );

  const [state, setState] = useState({
    userReactions: initialUserReactions,
    loading: false,
  });

  const { userReactions, loading } = state;

  const findUserReaction = (reaction: string) => {
    return userReactions.find((el: any) => el.reaction === reaction);
  };

  const hasUserReaction = (reaction: string) => !!findUserReaction(reaction);

  const addReact = async (reaction: string) => {
    if (loading || hasUserReaction(reaction)) {
      return;
    }

    const tempUuid = `temp-optimistic-uuid-${Date.now()}`;

    const optimisticReaction = {
      uuid: tempUuid,
      reaction,
      tempUuid,
      creator: user,
      reactionable: {
        uuid: initialData?.uuid,
      },
    };

    const previousUserReactions = userReactions;
    setState((prevState) => ({
      ...prevState,
      loading: true,
      userReactions: [...prevState.userReactions, optimisticReaction],
    }));

    onReactAdd(optimisticReaction);

    try {
      const res = await apiRequests.post(
        `${apiRoutes.COMMENTS}/${initialData?.uuid}/reaction`,
        {
          reaction,
        }
      );

      const newReactionData = {
        ...res.data.data,
        tempUuid: optimisticReaction.uuid,
      };

      setState((prevState) => ({
        ...prevState,
        loading: false,
        userReactions: prevState.userReactions
          .filter((el: any) => el.uuid !== optimisticReaction.uuid)
          .concat(newReactionData),
      }));

      onReactAdd(newReactionData);
    } catch (error) {
      console.error('Reaction add failed:', error);
      setState((prevState) => ({
        ...prevState,
        loading: false,
        userReactions: previousUserReactions,
      }));
    }
  };

  const removeReact = async (reactionShortcode: string) => {
    if (loading) {
      return;
    }

    const existingReaction = findUserReaction(reactionShortcode);

    if (!existingReaction) {
      return;
    }

    const previousUserReactions = userReactions;
    setState((prevState) => ({
      ...prevState,
      loading: true,
      userReactions: prevState.userReactions.filter(
        (el: any) => el.uuid !== existingReaction.uuid
      ),
    }));

    try {
      await apiRequests.delete(
        `${apiRoutes.REACTION}/${existingReaction.uuid}`
      );

      setState((prevState) => ({
        ...prevState,
        loading: false,
        userReactions: prevState.userReactions.filter(
          (el: any) => el.uuid !== existingReaction.uuid
        ),
      }));

      onReactRemove(existingReaction);
    } catch (error) {
      console.error('Reaction removal failed:', error);
      setState((prevState) => ({
        ...prevState,
        loading: false,
        userReactions: previousUserReactions,
      }));
    }
  };

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      userReactions: initialUserReactions,
    }));
  }, [initialUserReactions]);

  return {
    state,
    addReact,
    hasUserReaction,
    userReactions,
    removeReact,
  };
};

export default useCommentReactions;
